import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import OffersView from './OffersView';
import { useQuery } from 'react-query';
import dynamic from 'next/dynamic';
import OfferCard from './cards/OfferCard';
import { resetFilters } from '../../../redux/slices/offersSlice';
import { getNewOffers } from '../../../new-lib/apis';
import { removeNullUndefinedEmptyFromObject } from '../../../helpers/common';
import NawyLoadingIndicator from '../../../new-components/NawyLoadingIndicator';
import ErrorComponent from '../../../new-components/ui/ErrorComponent';
import AutoColumnsGrid from '../../../new-components/display-system/AutoColumnsGrid';

export interface AppliedFiltersInterface {
  compound_id?: number;
  developer_id?: number;
  area_id?: number;
  offer_filter?: string;
}

export interface OffersViewProps {
  offersCount?: number;
  getCardsContainer: () => JSX.Element;
  offersStartDate?: any;
}

interface CityscapeOffersContainerProps {
  offersStartDate?: any;
}

const CityscapeOffersContainer = ({
  offersStartDate
}: CityscapeOffersContainerProps) => {
  const isMobile = useSelector((state: any) => state.app.isMobile);

  const { locale } = useRouter();

  const dispatch = useDispatch();

  const handleResetFiltersClick = () => {
    dispatch(resetFilters());
  };

  const developerIds = useSelector((state: any) => state.offers.developers_ids);
  const areasIds = useSelector((state: any) => state.offers.areas_ids);
  const offersFilters = useSelector(
    (state: any) => state.offers.offers_filters
  );
  const isNawyExclusive = useSelector(
    (state: any) => state.offers.isNawyExclusive
  );

  const queryKey = [
    'offers',
    developerIds,
    areasIds,
    offersFilters,
    isNawyExclusive,
    locale
  ];
  const queryFn = () =>
    getNewOffers(
      removeNullUndefinedEmptyFromObject({
        ...(isNawyExclusive && { nawy_exclusive: 'true' }),
        ...(offersFilters.length > 0 && { offers_filters: offersFilters }),
        ...(areasIds.length > 0 && { areas_ids: areasIds }),
        ...(developerIds.length > 0 && { developers_ids: developerIds })
      }),
      locale
    );

  const { status, data } = useQuery({
    queryKey: queryKey,
    queryFn: queryFn,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: Infinity
  });

  const getCardsContainer = () => {
    if (status === 'loading') {
      return <NawyLoadingIndicator />;
    }

    if (status === 'error') {
      return <ErrorComponent />;
    }

    if (data.length > 0) {
      return (
        <AutoColumnsGrid
          columnsGap="24px"
          minWidth={isMobile ? '100%' : '330px'}
          rowsGap="16px"
        >
          {data?.map((offer: any) => (
            <OfferCard key={offer.id} offer={offer} />
          ))}
        </AutoColumnsGrid>
      );
    }

    if (data.length === 0) {
      const NoResultsIndicator = dynamic(
        () => import('../../../new-components/NoResultsIndicator')
      );
      return (
        <NoResultsIndicator
          onClick={handleResetFiltersClick}
          isResetButton={true}
        />
      );
    }

    return <></>;
  };

  return (
    <OffersView
      getCardsContainer={getCardsContainer}
      offersStartDate={offersStartDate}
    />
  );
};

export default CityscapeOffersContainer;
